import { useState } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { mockClients } from '@/data'
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  ChevronRight,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import type { Session } from '@/types'

interface SessionListViewProps {
  onSessionClick: (session: Session) => void
}

export function SessionListView({ onSessionClick }: SessionListViewProps) {
  const { sessions } = useSessionStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | Session['status']>('all')
  const [typeFilter, setTypeFilter] = useState<'all' | Session['type']>('all')
  const [sortBy, setSortBy] = useState<'date' | 'client' | 'type'>('date')

  const getClientName = (clientId: string) => {
    const client = mockClients.find(c => c.id === clientId)
    return client?.name || 'Unknown Client'
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date)
  }

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getStatusIcon = (status: Session['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'no-show':
        return <XCircle className="w-4 h-4 text-gray-500" />
      case 'in-progress':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
      default:
        return <Clock className="w-4 h-4 text-blue-500" />
    }
  }

  const getStatusColor = (status: Session['status']) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-50 text-blue-700 border-blue-200'
      case 'in-progress':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200'
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200'
      case 'no-show':
        return 'bg-gray-50 text-gray-700 border-gray-200'
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200'
    }
  }

  const filteredAndSortedSessions = () => {
    let filtered = sessions

    // Apply search filter
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase()
      filtered = filtered.filter(session => {
        const clientName = getClientName(session.clientId).toLowerCase()
        const sessionType = session.type.toLowerCase()
        const locationName = session.location?.name?.toLowerCase() || ''
        
        return (
          clientName.includes(lowercaseSearch) ||
          sessionType.includes(lowercaseSearch) ||
          locationName.includes(lowercaseSearch)
        )
      })
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(session => session.status === statusFilter)
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(session => session.type === typeFilter)
    }

    // Sort sessions
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          const dateCompare = a.date.getTime() - b.date.getTime()
          if (dateCompare === 0) {
            return a.startTime.localeCompare(b.startTime)
          }
          return dateCompare
        case 'client':
          return getClientName(a.clientId).localeCompare(getClientName(b.clientId))
        case 'type':
          return a.type.localeCompare(b.type)
        default:
          return 0
      }
    })

    return filtered
  }

  const filteredSessions = filteredAndSortedSessions()

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Filters and Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no-show">No Show</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="training">Training</option>
              <option value="consultation">Consultation</option>
              <option value="assessment">Assessment</option>
              <option value="check-in">Check-in</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="date">Sort by Date</option>
              <option value="client">Sort by Client</option>
              <option value="type">Sort by Type</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredSessions.length} of {sessions.length} sessions
        </div>
      </div>

      {/* Session List */}
      <div className="divide-y divide-gray-200">
        {filteredSessions.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters to see more sessions.'
                : 'Create your first session to get started.'
              }
            </p>
          </div>
        ) : (
          filteredSessions.map((session) => (
            <button
              key={session.id}
              onClick={() => onSessionClick(session)}
              className="w-full p-4 text-left hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {/* Header Row */}
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(session.status)}
                    <h3 className="font-medium text-gray-900 capitalize">
                      {session.type} Session
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(session.status)}`}>
                      {session.status.replace('-', ' ')}
                    </span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    {/* Client */}
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{getClientName(session.clientId)}</span>
                    </div>

                    {/* Date & Time */}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>
                        {formatDate(session.date)} at {formatTime(session.startTime)}
                      </span>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="capitalize">
                        {session.location?.type || 'TBD'}
                        {session.location?.name && ` - ${session.location.name}`}
                      </span>
                    </div>
                  </div>

                  {/* Notes Preview */}
                  {session.preSessionNotes && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {session.preSessionNotes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  )
}